import React, { useState, useMemo } from "react";
import Popup from "reactjs-popup";

const popupContentStyle = {
  border: "none",
  borderRadius: "12px",
  boxShadow: "0 20px 24px -4px rgba(10,13,18,0.08), 0 8px 8px -4px rgba(10,13,18,0.03), 0 3px 3px -1.5px rgba(10,13,18,0.04)",
  padding: "0px",
  background: "var(--od-surface, #fff)",
  width: "688px",
  maxWidth: "90vw",
  maxHeight: "80vh",
  display: "flex",
  flexDirection: "column",
};

const overlayStyle = {
  zIndex: "4000",
  backgroundColor: "rgba(10, 13, 18, 0.7)",
};

const KeyBadge = ({ children }) => (
  <span style={{
    backgroundColor: "var(--od-c-fafafa, #fafafa)",
    border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
    borderRadius: "4px",
    padding: "2px 4px",
    fontSize: "14px",
    fontWeight: "bold",
    color: "var(--od-c-535862, #535862)",
    minWidth: "24px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }}>
    {children}
  </span>
);

const ShortcutRow = ({ label, shortcuts }) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
    borderBottom: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
  }}>
    <span style={{ fontSize: "16px", color: "var(--od-c-333333, #333)" }}>{label}</span>
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {shortcuts.map((shortcut, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span style={{ fontSize: "14px", color: "var(--od-c-717680, #717680)", margin: "0 4px" }}>or</span>}
          <KeyBadge>{shortcut}</KeyBadge>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: "20px" }}>
    <h3 style={{
      fontSize: "18px",
      fontWeight: "600",
      color: "var(--od-c-333333, #333)",
      marginBottom: "0",
    }}>
      {title}
    </h3>
    {children}
  </div>
);

const SHORTCUTS = {
  navigation: [
    { label: "Previous Period", shortcuts: ["P"] },
    { label: "Next Period", shortcuts: ["N"] },
    { label: "Today", shortcuts: ["T"] },
    { label: "Go to date", shortcuts: ["G"] },
  ],
  views: [
    { label: "Day View", shortcuts: ["1", "D"] },
    { label: "Week View", shortcuts: ["2", "W"] },
    { label: "Month View", shortcuts: ["3", "M"] },
    { label: "List View", shortcuts: ["4", "L"] },
  ],
  actions: [
    { label: "Create new event (Hold)", shortcuts: ["C"] },
    { label: "Create Confirmed event", shortcuts: ["⇧ C"] },
    { label: "Create Hold", shortcuts: ["H"] },
    { label: "Search calendar", shortcuts: ["/"] },
    { label: "Check avails", shortcuts: ["A"] },
    { label: "Clear holds on a date", shortcuts: ["⇧ D"] },
    { label: "Toggle left sidebar", shortcuts: ["`"] },
  ],
  general: [
    { label: "Show keyboard shortcuts", shortcuts: ["?"] },
    { label: "Close modal / cancel action", shortcuts: ["Esc"] },
  ],
};

const KeyboardShortcutsModal = ({
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredShortcuts = useMemo(() => {
    if (!searchTerm.trim()) {
      return SHORTCUTS;
    }

    const term = searchTerm.toLowerCase();
    const filterSection = (items) =>
      items.filter(item =>
        item.label.toLowerCase().includes(term) ||
        item.shortcuts.some(s => s.toLowerCase().includes(term))
      );

    return {
      navigation: filterSection(SHORTCUTS.navigation),
      views: filterSection(SHORTCUTS.views),
      actions: filterSection(SHORTCUTS.actions),
      general: filterSection(SHORTCUTS.general),
    };
  }, [searchTerm]);

  const hasResults = Object.values(filteredShortcuts).some(section => section.length > 0);

  return (
    <Popup
      modal
      open={isOpen}
      onClose={onClose}
      contentStyle={popupContentStyle}
      overlayStyle={overlayStyle}
      closeOnDocumentClick
      closeOnEscape
    >
      {close => (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          {/* Header */}
          <div style={{
            padding: "24px 24px 20px 24px",
            borderBottom: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
            position: "relative",
          }}>
            <h2 style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "var(--od-c-333333, #333)",
              margin: "0",
            }}>
              Keyboard shortcuts
            </h2>
            <button
              onClick={close}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "8px",
              }}
            >
              <i className="fas fa-times" style={{ color: "var(--od-c-a4a7ae, #a4a7ae)", fontSize: "20px" }} />
            </button>
          </div>

          {/* Content */}
          <div style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
          }}>
            {/* Search */}
            <div style={{
              marginBottom: "20px",
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                border: "1px solid var(--od-c-d5d7da, #d5d7da)",
                borderRadius: "8px",
                backgroundColor: "var(--od-c-ffffff, white)",
                boxShadow: "0 1px 2px rgba(10, 13, 18, 0.05)",
              }}>
                <i className="fas fa-search" style={{ color: "var(--od-c-717680, #717680)", fontSize: "16px" }} />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    flex: 1,
                    fontSize: "16px",
                    backgroundColor: "transparent",
                    color: "var(--od-c-333333, #333)",
                  }}
                />
              </div>
            </div>

            {!hasResults ? (
              <div style={{ textAlign: "center", color: "var(--od-c-717680, #717680)", padding: "40px 0" }}>
                No shortcuts found matching "{searchTerm}"
              </div>
            ) : (
              <>
                {filteredShortcuts.navigation.length > 0 && (
                  <Section title="Navigation">
                    {filteredShortcuts.navigation.map((item, index) => (
                      <ShortcutRow key={index} label={item.label} shortcuts={item.shortcuts} />
                    ))}
                  </Section>
                )}

                {filteredShortcuts.views.length > 0 && (
                  <Section title="Views">
                    {filteredShortcuts.views.map((item, index) => (
                      <ShortcutRow key={index} label={item.label} shortcuts={item.shortcuts} />
                    ))}
                  </Section>
                )}

                {filteredShortcuts.actions.length > 0 && (
                  <Section title="Actions">
                    {filteredShortcuts.actions.map((item, index) => (
                      <ShortcutRow key={index} label={item.label} shortcuts={item.shortcuts} />
                    ))}
                  </Section>
                )}

                {filteredShortcuts.general.length > 0 && (
                  <Section title="General">
                    {filteredShortcuts.general.map((item, index) => (
                      <ShortcutRow key={index} label={item.label} shortcuts={item.shortcuts} />
                    ))}
                  </Section>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Popup>
  );
};

export default KeyboardShortcutsModal;
